import { Header, Icon, type IconName, Sheet } from "@caint/design-system";
import type { PapelKey, UsuarioPublico } from "@caint/shared";
import { gerarSenhaProvisoria } from "@caint/shared";
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CredencialPronta } from "../components/CredencialPronta.tsx";
import { UsuarioCampos } from "../components/usuarios/UsuarioCampos.tsx";
import { Avatar, Destaque, EixoChip, PerfilBadge, StatusPill } from "../components/usuarios/atomos.tsx";
import { nomeUnidade } from "../lib/ciclos.ts";
import { mensagemAmigavel } from "../lib/erros.ts";
import { nomeCargo } from "../lib/planoUnidade.ts";
import { useUsuarioForm } from "../lib/useUsuarioForm.ts";
import {
  type ContagemUsuarios,
  EIXOS,
  FILTROS_VAZIOS,
  type FiltrosUsuarios,
  ORDEM_PADRAO,
  PAPEIS_ORDEM,
  type StatusFiltro,
  contarFiltrosAtivos,
  contarUsuarios,
  criadaEm,
  filtrarUsuarios,
  ordenarUsuarios,
  rotuloPapelCurto,
  statusUsuario,
  temFiltro,
} from "../lib/usuariosLista.ts";
import type { UsuariosApi } from "../state/useAppState.ts";
import "./usuarios-mobile.css";

export interface UsuariosProps {
  papel: PapelKey;
  api: UsuariosApi;
  onBack: () => void;
  /** Id do gestor logado: marca a própria conta e impede que ele se desative. */
  euId?: string;
}

const GESTORES = new Set<PapelKey>(["admin", "coord"]);
/** Quantos usuários a lista mostra por vez ("Mostrar mais" soma outro lote). */
const LOTE = 30;
/** A busca também acha por unidade e cargo — a lib recebe os resolvedores. */
const OPCOES_BUSCA = { nomeUnidade: (id: string) => nomeUnidade(id), nomeCargo };

type Folha =
  | { tipo: "novo" }
  | { tipo: "filtros" }
  | { tipo: "acoes"; usuario: UsuarioPublico }
  | { tipo: "editar"; usuario: UsuarioPublico }
  | { tipo: "senha"; usuario: UsuarioPublico }
  | { tipo: "mensagem"; usuario: UsuarioPublico }
  | { tipo: "desativar"; usuario: UsuarioPublico };

export function Usuarios({ papel, api, onBack, euId }: UsuariosProps) {
  const [lista, setLista] = useState<UsuarioPublico[] | null>(null);
  /** Falha ao CARREGAR a lista (a tela vira estado de erro com "tentar de novo"). */
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  /** Falha de uma AÇÃO sobre a lista já carregada (faixa dispensável). */
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ id: number; texto: string } | null>(null);
  const [folha, setFolha] = useState<Folha | null>(null);
  const [filtros, setFiltros] = useState<FiltrosUsuarios>(FILTROS_VAZIOS);
  const [limite, setLimite] = useState(LOTE);
  const podeGerir = GESTORES.has(papel);

  const jaCarregou = useRef(false);
  const carregar = useCallback(async () => {
    setErroCarga(null);
    try {
      setLista(await api.listar());
      jaCarregou.current = true;
    } catch (e) {
      // Recarga que falha não apaga a lista que já estava na tela.
      if (jaCarregou.current) {
        setErro(mensagemAmigavel(e));
      } else {
        setErroCarga(mensagemAmigavel(e));
      }
    }
  }, [api]);

  useEffect(() => {
    if (podeGerir) {
      void carregar();
    }
  }, [podeGerir, carregar]);

  const avisar = useCallback((texto: string) => setAviso({ id: Date.now(), texto }), []);
  useEffect(() => {
    if (!aviso) {
      return;
    }
    const t = window.setTimeout(() => setAviso(null), 4000);
    return () => window.clearTimeout(t);
  }, [aviso]);

  /** Todo filtro novo volta ao primeiro lote. */
  const filtrar = useCallback((parcial: Partial<FiltrosUsuarios>) => {
    setFiltros((f) => ({ ...f, ...parcial }));
    setLimite(LOTE);
  }, []);
  const limparFiltros = useCallback(() => {
    setFiltros(FILTROS_VAZIOS);
    setLimite(LOTE);
  }, []);

  const contagem = useMemo(() => contarUsuarios(lista ?? []), [lista]);
  const visiveis = useMemo(
    () => ordenarUsuarios(filtrarUsuarios(lista ?? [], filtros, OPCOES_BUSCA), ORDEM_PADRAO),
    [lista, filtros],
  );

  /**
   * Recarrega a lista SEM fechar a folha. Criar usuário e redefinir senha
   * terminam mostrando a credencial — a senha só existe em claro naquele
   * instante, então fechar ali obrigaria a redefinir de novo. Quem fecha é o
   * "Concluir", depois de a pessoa copiar a mensagem.
   */
  const aposAcaoSemFechar = useCallback(
    (texto: string) => {
      avisar(texto);
      void carregar();
    },
    [avisar, carregar],
  );

  const alternarAtivo = useCallback(
    async (u: UsuarioPublico, ativo: boolean) => {
      setErro(null);
      await api.atualizar(u.id, { ativo });
      avisar(ativo ? `Conta de ${u.nome} reativada.` : `Conta de ${u.nome} desativada.`);
      await carregar();
    },
    [api, avisar, carregar],
  );

  const fechar = useCallback(() => setFolha(null), []);
  /**
   * Fecha SÓ se a folha aberta ainda for aquela. A resposta de uma ação pode
   * chegar depois de o gestor já ter aberto outra folha — e um `setFolha(null)`
   * cego fecharia a folha errada, levando junto o que estava sendo digitado.
   */
  const fecharEsta = useCallback((esta: Folha) => setFolha((f) => (f === esta ? null : f)), []);

  if (!podeGerir) {
    return (
      <Fragment>
        <Header title="Usuários & Perfis" sub="Gestão de acesso" onBack={onBack} />
        <div className="m-body">
          <p className="spec" style={{ textAlign: "center", marginTop: 40 }}>
            Acesso restrito à Coordenação e à Administração.
          </p>
        </div>
      </Fragment>
    );
  }

  // O status tem os chips sempre à vista; o contador do botão conta só o que mora na folha.
  const nFiltros = contarFiltrosAtivos({ ...filtros, status: "todos" });

  return (
    <Fragment>
      <Header
        title="Usuários & Perfis"
        sub={lista ? resumoContas(contagem) : "Gestão de acesso"}
        onBack={onBack}
        action={
          <button
            type="button"
            className="usm-novo"
            aria-label="Novo usuário"
            onClick={() => setFolha({ tipo: "novo" })}
          >
            <Icon name="plus" size={18} stroke={2.4} />
            Novo
          </button>
        }
      />

      <div className="usm-topo">
        <div className="usm-busca-linha">
          <div className="usm-busca">
            <span className="usm-busca-lupa">
              <Icon name="search" size={18} />
            </span>
            <input
              type="search"
              value={filtros.busca}
              placeholder="Buscar nome, e-mail, perfil…"
              aria-label="Buscar usuários"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="search"
              onChange={(e) => filtrar({ busca: e.target.value })}
            />
            {filtros.busca && (
              <button
                type="button"
                className="usm-busca-limpar"
                aria-label="Limpar busca"
                onClick={() => filtrar({ busca: "" })}
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
          <button
            type="button"
            className={`usm-filtros-btn${nFiltros > 0 ? " on" : ""}`}
            aria-label={nFiltros > 0 ? `Filtros (${nFiltros} ativos)` : "Filtros"}
            onClick={() => setFolha({ tipo: "filtros" })}
          >
            <Icon name="filter" size={17} />
            {nFiltros > 0 && <span className="usm-filtros-n">{nFiltros}</span>}
          </button>
        </div>
        <ChipsStatus
          status={filtros.status}
          contagem={lista ? contagem : null}
          onMudar={(status) => filtrar({ status })}
        />
      </div>

      <div className="m-body usm-body">
        {erro && (
          <div className="usm-erro" role="alert">
            <span>{erro}</span>
            <button type="button" aria-label="Dispensar aviso" onClick={() => setErro(null)}>
              <Icon name="x" size={16} />
            </button>
          </div>
        )}

        <Conteudo
          carregando={lista === null}
          erroCarga={erroCarga}
          totalGeral={contagem.total}
          visiveis={visiveis}
          limite={limite}
          busca={filtros.busca}
          euId={euId}
          onTentarDeNovo={() => void carregar()}
          onLimpar={limparFiltros}
          onMais={() => setLimite((n) => n + LOTE)}
          onAbrir={setFolha}
        />
      </div>

      {/* A região viva fica SEMPRE montada: leitor de tela anuncia o que muda
          dentro dela, não um elemento que já nasce com o texto. */}
      <div aria-live="polite">
        {aviso && (
          <div className="m-toast usm-toast" key={aviso.id}>
            {aviso.texto}
          </div>
        )}
      </div>

      {folha?.tipo === "filtros" && (
        <FiltrosSheet
          filtros={filtros}
          contagem={contagem}
          total={visiveis.length}
          onFiltrar={filtrar}
          onLimpar={limparFiltros}
          onClose={fechar}
        />
      )}
      {folha?.tipo === "acoes" && (
        <AcoesSheet
          u={folha.usuario}
          ehEu={folha.usuario.id === euId}
          onClose={fechar}
          onIr={setFolha}
          onReativar={async (u) => {
            fechar();
            try {
              await alternarAtivo(u, true);
            } catch (e) {
              setErro(mensagemAmigavel(e));
            }
          }}
        />
      )}
      {folha?.tipo === "novo" && (
        <FormSheet
          api={api}
          onClose={fechar}
          onSalvo={(nome) => aposAcaoSemFechar(`Usuário ${nome} criado.`)}
        />
      )}
      {folha?.tipo === "editar" && (
        <FormSheet
          key={folha.usuario.id}
          api={api}
          editando={folha.usuario}
          euId={euId}
          onClose={fechar}
          onSalvo={(nome) => {
            fecharEsta(folha);
            aposAcaoSemFechar(`Dados de ${nome} atualizados.`);
          }}
        />
      )}
      {folha?.tipo === "mensagem" && (
        <MensagemSheet
          usuario={folha.usuario}
          api={api}
          onClose={fechar}
          onNovaSenha={() => setFolha({ tipo: "senha", usuario: folha.usuario })}
        />
      )}
      {folha?.tipo === "senha" && (
        <SenhaSheet
          usuario={folha.usuario}
          api={api}
          onClose={fechar}
          onPronto={() => aposAcaoSemFechar("Senha redefinida.")}
        />
      )}
      {folha?.tipo === "desativar" && (
        <DesativarSheet
          usuario={folha.usuario}
          onClose={fechar}
          onConfirmar={async () => {
            await alternarAtivo(folha.usuario, false);
            fecharEsta(folha);
          }}
        />
      )}
    </Fragment>
  );
}

function resumoContas(c: ContagemUsuarios): string {
  return `${c.total} ${c.total === 1 ? "conta" : "contas"} · ${c.ativos} ${c.ativos === 1 ? "ativa" : "ativas"}`;
}

/* -------------------------------------------------------------------- lista */

/** Miolo da tela: erro de carga, esqueleto, vazio, sem resultado ou a lista. */
function Conteudo({
  carregando,
  erroCarga,
  totalGeral,
  visiveis,
  limite,
  busca,
  euId,
  onTentarDeNovo,
  onLimpar,
  onMais,
  onAbrir,
}: {
  carregando: boolean;
  erroCarga: string | null;
  totalGeral: number;
  visiveis: readonly UsuarioPublico[];
  limite: number;
  busca: string;
  euId?: string;
  onTentarDeNovo: () => void;
  onLimpar: () => void;
  onMais: () => void;
  onAbrir: (f: Folha) => void;
}) {
  if (erroCarga) {
    return (
      <Estado
        icone="alert"
        titulo="Não foi possível carregar"
        texto={erroCarga}
        acao={
          <button type="button" className="m-btn sm ghost" onClick={onTentarDeNovo}>
            Tentar novamente
          </button>
        }
      />
    );
  }
  if (carregando) {
    return (
      <ul className="usm-lista" aria-busy="true" aria-label="Carregando usuários">
        {[62, 48, 70, 54, 66, 50].map((w) => (
          <li key={w} className="usm-linha usm-linha-esq">
            <span className="usm-esq usm-esq-avatar" />
            <span className="usm-txt">
              <span className="usm-esq" style={{ width: `${w}%` }} />
              <span className="usm-esq" style={{ width: `${w + 16}%`, marginTop: 9 }} />
              <span className="usm-esq usm-esq-pilula" style={{ marginTop: 11 }} />
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (totalGeral === 0) {
    return (
      <Estado
        icone="people"
        titulo="Nenhum usuário cadastrado"
        texto="Crie a primeira conta para dar acesso ao sistema."
        acao={
          <button type="button" className="m-btn sm primary" onClick={() => onAbrir({ tipo: "novo" })}>
            Novo usuário
          </button>
        }
      />
    );
  }
  if (visiveis.length === 0) {
    return (
      <Estado
        icone="search"
        titulo="Nenhum usuário encontrado"
        texto={
          busca.trim()
            ? `Nada corresponde a “${busca.trim()}” com os filtros atuais.`
            : "Nenhuma conta corresponde aos filtros atuais."
        }
        acao={
          <button type="button" className="m-btn sm ghost" onClick={onLimpar}>
            Limpar filtros
          </button>
        }
      />
    );
  }
  const pagina = visiveis.slice(0, limite);
  const restam = visiveis.length - pagina.length;
  return (
    <>
      <ul className="usm-lista">
        {pagina.map((u) => (
          <LinhaUsuario
            key={u.id}
            u={u}
            busca={busca}
            ehEu={u.id === euId}
            onEditar={() => onAbrir({ tipo: "editar", usuario: u })}
            onAcoes={() => onAbrir({ tipo: "acoes", usuario: u })}
          />
        ))}
      </ul>
      <p className="usm-contagem" aria-live="polite">
        Mostrando {pagina.length} de {visiveis.length}
        {visiveis.length !== totalGeral ? ` · filtrado de ${totalGeral}` : ""}
      </p>
      {restam > 0 && (
        <button type="button" className="m-btn ghost" onClick={onMais}>
          Mostrar mais {Math.min(LOTE, restam)}
        </button>
      )}
    </>
  );
}

function ChipsStatus({
  status,
  contagem,
  onMudar,
}: {
  status: StatusFiltro;
  /** `null` enquanto a lista carrega: chips sem número (zero seria mentira). */
  contagem: ContagemUsuarios | null;
  onMudar: (s: StatusFiltro) => void;
}) {
  const chips: { chave: StatusFiltro; rotulo: string; n: number | undefined }[] = [
    { chave: "todos", rotulo: "Todos", n: contagem?.total },
    { chave: "ativos", rotulo: "Ativos", n: contagem?.ativos },
    { chave: "inativos", rotulo: "Inativos", n: contagem?.inativos },
  ];
  if ((contagem?.pendentes ?? 0) > 0 || status === "pendentes") {
    chips.push({ chave: "pendentes", rotulo: "Acesso pendente", n: contagem?.pendentes });
  }
  return (
    // biome-ignore lint/a11y/useSemanticElements: grupo de botões de alternância, não um <fieldset> de formulário
    <div className="usm-chips" role="group" aria-label="Filtrar por status">
      {chips.map((c) => (
        <button
          key={c.chave}
          type="button"
          className={`usm-chip${status === c.chave ? " on" : ""}`}
          aria-pressed={status === c.chave}
          onClick={() => onMudar(c.chave)}
        >
          {c.rotulo}
          {c.n !== undefined && <span className="usm-chip-n">{c.n}</span>}
        </button>
      ))}
    </div>
  );
}

function LinhaUsuario({
  u,
  busca,
  ehEu,
  onEditar,
  onAcoes,
}: {
  u: UsuarioPublico;
  busca: string;
  ehEu: boolean;
  onEditar: () => void;
  onAcoes: () => void;
}) {
  const idDados = useId();
  const status = statusUsuario(u);
  const vinculo =
    u.papel === "unidade"
      ? [nomeCargo(u.cargoUnidade), u.unidadeId ? nomeUnidade(u.unidadeId) : ""].filter(Boolean).join(" · ")
      : "";
  return (
    <li className={`usm-linha${u.ativo ? "" : " inativa"}`}>
      <button
        type="button"
        className="usm-linha-btn"
        aria-label={`Editar ${u.nome}`}
        // O aria-label sozinho esconderia e-mail, perfil e status do leitor de
        // tela; a descrição devolve esses dados depois do nome do botão.
        aria-describedby={idDados}
        onClick={onEditar}
      >
        <Avatar nome={u.nome} id={u.id} inativo={!u.ativo} tamanho={40} />
        <span className="usm-txt">
          <span className="usm-nome">
            <span className="usm-corta">
              <Destaque texto={u.nome} busca={busca} />
            </span>
            {ehEu && <span className="usm-voce">Você</span>}
          </span>
          <span className="usm-dados" id={idDados}>
            <span className="usm-email usm-corta">
              <Destaque texto={u.matricula} busca={busca} />
            </span>
            <span className="usm-selos">
              <PerfilBadge papel={u.papel} />
              {u.eixo && <EixoChip eixo={u.eixo} />}
              {/* "Ativo" é o normal: só o que foge dele ganha selo, para não poluir a lista. */}
              {status !== "ativo" && <StatusPill status={status} />}
            </span>
            {vinculo && (
              <span className="usm-vinculo usm-corta">
                <Destaque texto={vinculo} busca={busca} />
              </span>
            )}
          </span>
        </span>
      </button>
      <button type="button" className="usm-mais" aria-label={`Mais ações de ${u.nome}`} onClick={onAcoes}>
        <Icon name="dots" size={20} />
      </button>
    </li>
  );
}

function Estado({
  icone,
  titulo,
  texto,
  acao,
}: {
  icone: IconName;
  titulo: string;
  texto: string;
  acao?: React.ReactNode;
}) {
  return (
    <div className="usm-estado">
      <span className="usm-estado-ic">
        <Icon name={icone} size={22} />
      </span>
      <b>{titulo}</b>
      <p>{texto}</p>
      {acao}
    </div>
  );
}

/* ------------------------------------------------------------------- folhas */

function FiltrosSheet({
  filtros,
  contagem,
  total,
  onFiltrar,
  onLimpar,
  onClose,
}: {
  filtros: FiltrosUsuarios;
  contagem: ContagemUsuarios;
  /** Quantos usuários a combinação atual devolve — vai no botão de confirmar. */
  total: number;
  onFiltrar: (parcial: Partial<FiltrosUsuarios>) => void;
  onLimpar: () => void;
  onClose: () => void;
}) {
  // A unidade SELECIONADA entra mesmo sem contas (a conta foi movida): senão o
  // select mostraria "Todas as unidades" com o filtro ainda ligado.
  const unidades = useMemo(() => {
    const porUnidade = { ...contagem.porUnidade };
    if (filtros.unidadeId && porUnidade[filtros.unidadeId] === undefined) {
      porUnidade[filtros.unidadeId] = 0;
    }
    return Object.entries(porUnidade)
      .map(([id, n]) => ({ id, n, nome: nomeUnidade(id) }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [contagem, filtros.unidadeId]);
  const alternarPapel = (p: string) =>
    onFiltrar({
      papeis: filtros.papeis.includes(p) ? filtros.papeis.filter((x) => x !== p) : [...filtros.papeis, p],
    });
  const eixos: { valor: string; rotulo: string; n: number | undefined }[] = [
    { valor: "", rotulo: "Todos", n: undefined },
    ...EIXOS.map((x) => ({ valor: x as string, rotulo: x as string, n: contagem.porEixo[x] })),
    { valor: "sem", rotulo: "Sem eixo", n: contagem.porEixo.sem },
  ];

  return (
    <Sheet
      title="Filtros"
      sub="A lista atualiza enquanto você escolhe"
      onClose={onClose}
      foot={
        <div className="usm-foot-duplo">
          <button type="button" className="m-btn ghost" disabled={!temFiltro(filtros)} onClick={onLimpar}>
            Limpar
          </button>
          <button type="button" className="m-btn primary" onClick={onClose}>
            Ver {total} {total === 1 ? "usuário" : "usuários"}
          </button>
        </div>
      }
    >
      <div className="usm-f-titulo">Perfil</div>
      <div className="usm-f-chips">
        {PAPEIS_ORDEM.map((p) => (
          <button
            key={p}
            type="button"
            className={`usm-f-chip${filtros.papeis.includes(p) ? " on" : ""}`}
            aria-pressed={filtros.papeis.includes(p)}
            onClick={() => alternarPapel(p)}
          >
            {rotuloPapelCurto(p)}
            <span className="usm-chip-n">{contagem.porPapel[p] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="usm-f-titulo">Eixo</div>
      <div className="usm-f-chips">
        {eixos.map((e) => (
          <button
            key={e.valor || "todos"}
            type="button"
            className={`usm-f-chip${filtros.eixo === e.valor ? " on" : ""}`}
            aria-pressed={filtros.eixo === e.valor}
            onClick={() => onFiltrar({ eixo: e.valor })}
          >
            {e.rotulo}
            {e.n !== undefined && <span className="usm-chip-n">{e.n}</span>}
          </button>
        ))}
      </div>

      {unidades.length > 0 && (
        <>
          <label className="usm-f-titulo" htmlFor="usm-f-unidade">
            Unidade
          </label>
          <select
            id="usm-f-unidade"
            className="m-select"
            value={filtros.unidadeId}
            onChange={(e) => onFiltrar({ unidadeId: e.target.value })}
          >
            <option value="">Todas as unidades</option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome} ({u.n})
              </option>
            ))}
          </select>
        </>
      )}
    </Sheet>
  );
}

function AcoesSheet({
  u,
  ehEu,
  onClose,
  onIr,
  onReativar,
}: {
  u: UsuarioPublico;
  ehEu: boolean;
  onClose: () => void;
  onIr: (f: Folha) => void;
  onReativar: (u: UsuarioPublico) => void;
}) {
  return (
    <Sheet title={u.nome} sub={u.matricula} onClose={onClose}>
      <div className="usm-acoes">
        <Acao icone="edit" rotulo="Editar dados" onClick={() => onIr({ tipo: "editar", usuario: u })} />
        <Acao
          icone="mail"
          rotulo="Mensagem de acesso"
          dica="Reenviar como entrar no sistema"
          onClick={() => onIr({ tipo: "mensagem", usuario: u })}
        />
        <Acao
          icone="key"
          rotulo="Redefinir senha"
          dica="Gera uma nova senha provisória"
          onClick={() => onIr({ tipo: "senha", usuario: u })}
        />
        {u.ativo ? (
          <Acao
            icone="power"
            rotulo="Desativar conta"
            dica={ehEu ? "Você não pode desativar a própria conta" : "A pessoa deixa de conseguir entrar"}
            perigo
            desabilitada={ehEu}
            onClick={() => onIr({ tipo: "desativar", usuario: u })}
          />
        ) : (
          <Acao
            icone="userCheck"
            rotulo="Reativar conta"
            dica="A pessoa volta a conseguir entrar"
            onClick={() => onReativar(u)}
          />
        )}
      </div>
    </Sheet>
  );
}

function Acao({
  icone,
  rotulo,
  dica,
  perigo,
  desabilitada,
  onClick,
}: {
  icone: IconName;
  rotulo: string;
  dica?: string;
  perigo?: boolean;
  desabilitada?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`usm-acao${perigo ? " perigo" : ""}`}
      disabled={desabilitada}
      onClick={onClick}
    >
      <span className="usm-acao-ic">
        <Icon name={icone} size={18} />
      </span>
      <span className="usm-acao-txt">
        <b>{rotulo}</b>
        {dica && <small>{dica}</small>}
      </span>
    </button>
  );
}

function FormSheet({
  api,
  editando,
  euId,
  onClose,
  onSalvo,
}: {
  api: UsuariosApi;
  editando?: UsuarioPublico;
  euId?: string;
  onClose: () => void;
  onSalvo: (nome: string) => void;
}) {
  const f = useUsuarioForm({ api, editando, onSalvo, onFechar: onClose });
  // Com a requisição em andamento a folha não fecha: a resposta chegaria a uma
  // folha que não existe mais e, na criação, a credencial nunca apareceria.
  const fecharSeLivre = () => {
    if (!f.salvando) {
      onClose();
    }
  };

  if (f.criado) {
    return (
      <Sheet
        title="Usuário criado"
        sub={f.criado.nome}
        onClose={onClose}
        // A senha só aparece aqui: um toque distraído fora não pode levá-la embora.
        closeOnScrim={false}
        foot={
          <button type="button" className="m-btn primary" onClick={onClose}>
            Concluir
          </button>
        }
      >
        <CredencialPronta {...f.criado} />
      </Sheet>
    );
  }

  return (
    <Sheet
      title={f.ehEdicao ? "Editar usuário" : "Novo usuário"}
      sub={editando ? subtituloEdicao(editando) : "A pessoa recebe o acesso por mensagem."}
      onClose={fecharSeLivre}
      // Com algo digitado, tocar fora não descarta o formulário.
      closeOnScrim={!f.sujo}
      foot={
        <button type="button" className="m-btn primary" disabled={f.salvando} onClick={() => void f.salvar()}>
          {f.salvando ? "Salvando…" : f.ehEdicao ? "Salvar alterações" : "Criar usuário"}
        </button>
      }
    >
      <form
        // A validação é a do formulário (`faltasUsuario`), não a nativa: o
        // `type="email"` barraria no "Ir" um login antigo que não é e-mail.
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!f.salvando) {
            void f.salvar();
          }
        }}
      >
        <UsuarioCampos f={f} idPrefixo="u" proprioUsuario={!!editando && editando.id === euId} />
        {/* "Ir" do teclado salva (o botão visível mora no rodapé, fora do <form>). */}
        <button type="submit" hidden tabIndex={-1} />
      </form>
    </Sheet>
  );
}

/** "ana@… · conta criada em 12/03/2026" — o e-mail ATUAL fica à vista enquanto se edita. */
function subtituloEdicao(u: UsuarioPublico): string {
  const data = criadaEm(u);
  return data ? `${u.matricula} · conta criada em ${data}` : u.matricula;
}

function MensagemSheet({
  usuario,
  api,
  onClose,
  onNovaSenha,
}: {
  usuario: UsuarioPublico;
  api: UsuariosApi;
  onClose: () => void;
  onNovaSenha: () => void;
}) {
  // `undefined` = buscando; `null` = não existe mais (já entrou ou trocou).
  const [senha, setSenha] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let vivo = true;
    setSenha(undefined);
    // try/catch em vez de `.catch`: um throw SÍNCRONO não seria pego pela
    // cadeia de promessa e deixaria a tela presa em "Buscando a senha…".
    void (async () => {
      try {
        const r = await api.senhaProvisoria(usuario.id);
        if (vivo) {
          setSenha(r.senha);
        }
      } catch {
        // Cai na mensagem SEM senha — o mesmo que se mostra quando ela não existe.
        if (vivo) {
          setSenha(null);
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, [usuario.id, api]);

  return (
    <Sheet
      title="Mensagem de acesso"
      sub={usuario.nome}
      onClose={onClose}
      foot={
        <div className="usm-foot-duplo">
          {/* Atalho para o caso real: a pessoa esqueceu a senha. */}
          <button type="button" className="m-btn ghost" onClick={onNovaSenha}>
            Gerar nova senha
          </button>
          <button type="button" className="m-btn primary" onClick={onClose}>
            Fechar
          </button>
        </div>
      }
    >
      <CredencialPronta
        nome={usuario.nome}
        matricula={usuario.matricula}
        senha={senha ?? undefined}
        carregandoSenha={senha === undefined}
        papel={usuario.papel}
        unidadeNome={usuario.papel === "unidade" ? nomeUnidade(usuario.unidadeId ?? undefined) : undefined}
      />
    </Sheet>
  );
}

function SenhaSheet({
  usuario,
  api,
  onClose,
  onPronto,
}: { usuario: UsuarioPublico; api: UsuariosApi; onClose: () => void; onPronto: () => void }) {
  const [senha, setSenha] = useState(() => gerarSenhaProvisoria());
  const [redefinida, setRedefinida] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await api.redefinirSenha(usuario.id, senha);
      // Não fecha: como na criação, é a única chance de ler a senha.
      setRedefinida(true);
      setSalvando(false);
      onPronto();
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setSalvando(false);
    }
  };

  if (redefinida) {
    return (
      <Sheet
        title="Senha redefinida"
        sub={usuario.nome}
        onClose={onClose}
        closeOnScrim={false}
        foot={
          <button type="button" className="m-btn primary" onClick={onClose}>
            Concluir
          </button>
        }
      >
        <CredencialPronta
          nome={usuario.nome}
          matricula={usuario.matricula}
          senha={senha}
          papel={usuario.papel}
          unidadeNome={usuario.papel === "unidade" ? nomeUnidade(usuario.unidadeId ?? undefined) : undefined}
          redefinicao
        />
      </Sheet>
    );
  }

  const curta = senha.length < 8;
  return (
    <Sheet
      title="Redefinir senha"
      sub={usuario.nome}
      // Salvando: não fecha, senão a senha nova é gravada e a credencial nunca aparece.
      onClose={() => {
        if (!salvando) {
          onClose();
        }
      }}
      foot={
        <button
          type="button"
          className="m-btn primary"
          disabled={salvando || curta}
          onClick={() => void salvar()}
        >
          {salvando ? "Salvando…" : "Definir nova senha"}
        </button>
      }
    >
      <div className="uf">
        {erro && (
          <p className="uf-alerta" role="alert">
            <Icon name="alert" size={16} />
            <span>{erro}</span>
          </p>
        )}
        <div className="m-field">
          <label htmlFor="s-senha">Nova senha provisória (mín. 8)</label>
          <div className="uf-linha">
            <input
              id="s-senha"
              className={`m-input uf-mono${curta ? " missing" : ""}`}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={curta || undefined}
              aria-describedby="s-senha-dica"
            />
            <button type="button" className="m-btn uf-gerar" onClick={() => setSenha(gerarSenhaProvisoria())}>
              <Icon name="refresh" size={16} />
              Gerar
            </button>
          </div>
          <p className={curta ? "u-erro-campo" : "uf-dica"} id="s-senha-dica">
            {curta
              ? "A senha precisa ter no mínimo 8 caracteres."
              : "A senha atual deixa de funcionar. Em seguida você recebe a mensagem pronta para enviar."}
          </p>
        </div>
      </div>
    </Sheet>
  );
}

function DesativarSheet({
  usuario,
  onClose,
  onConfirmar,
}: { usuario: UsuarioPublico; onClose: () => void; onConfirmar: () => Promise<void> }) {
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const confirmar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await onConfirmar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
      setSalvando(false);
    }
  };

  return (
    <Sheet
      title="Desativar esta conta?"
      sub={usuario.matricula}
      onClose={() => {
        if (!salvando) {
          onClose();
        }
      }}
      foot={
        <div className="usm-foot-duplo">
          <button type="button" className="m-btn ghost" disabled={salvando} onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="m-btn danger" disabled={salvando} onClick={() => void confirmar()}>
            {salvando ? "Desativando…" : "Desativar conta"}
          </button>
        </div>
      }
    >
      <div className="uf">
        {erro && (
          <p className="uf-alerta" role="alert">
            <Icon name="alert" size={16} />
            <span>{erro}</span>
          </p>
        )}
        <p className="usm-confirma">
          <b>{usuario.nome}</b> não vai mais conseguir entrar no CAINT. Nada é apagado — o histórico da conta
          continua como está, e você pode reativá-la quando quiser.
        </p>
      </div>
    </Sheet>
  );
}
